<template>
  <div class="variant-carousel" :class="{ 'is-loading': isLoading }">
    <div class="variant-carousel__header">
      <div class="variant-carousel__swatch-info">
        <div
          v-if="getColourStyle() || getColourImageStyle()"
          class="variant-carousel__colour-image"
          :style="getColourStyle()"
        >
          <span
            class="variant-carousel__colour-image"
            :style="getColourImageStyle()"
          />

        </div>

        <p
          v-if="variantColourTitle"
          class="
            product-colour-selector__title
            text-body-xs-bold
            text-body-s-bold-desktop
          "
          v-text="variantColourTitle"
        />
      </div>

      <div class="variant-carousel__variants-info">
        <p
          v-if="variantColourTotal && variantColourTotal > 0"
          class="
            product-colour-selector__subtitle
            text-body-xs-regular
            text-body-s-regular-desktop
          "
          v-text="$string(
            'product.colours_available_ab',
            {
              replace: { num: variantColourTotal},
              pluralise: variantColourTotal,
            }
          )"
        />
      </div>
    </div>

    <splide
      v-if="swatches.length > 0"
      class="variant-carousel__carousel"
      :options="splideOptions"
      ref="variantSplide"
      :has-track="false"
      aria-label="Variant carousel"
    >
    <splide-track>
        <splide-slide
          v-for="swatch of swatches"
          :key="`${swatch.id}-${swatch.url}`"
        >
          <div
            class="variant-carousel__swatch"
            :class="[
                {
                  [$class('active')]: swatch.id === product.id,
                }
              ]"
            @click.prevent.stop="handleVariantChange(swatch)"
          >
            <div
              class="variant-carousel__link"
            >
              <div class="variant-carousel__swatch-image">
                <img
                  v-if="swatch.image"
                  :src="swatch.image"
                  :alt="swatch.title"
                  sizes="120px"
                >
              </div>
            </div>
          </div>
        </splide-slide>
      </splide-track>

      <div
        class="variant-carousel__arrows splide__arrows"
      >
        <button
          class="splide__arrow splide__arrow--prev"
          :class="{ 'splide__arrow--hide': !showPrevArrow }"

          type="button"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.previous')"
          />

          <icon-arrow-left />
        </button>

        <button
          class="splide__arrow splide__arrow--next"
          :class="{ 'splide__arrow--hide': showNextArrow }"
          type="button"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.next')"
          />

          <icon-arrow-right />
        </button>
      </div>

    </splide>
  </div>
</template>

<script>

/**
 * Vue: Variant carousel (variant-carousel)
 * -----------------------------------------------------------------------------
 *
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Array} swatches - Array of objects containing swatches to be rendered
 *
 */
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import IconArrowLeft from '~icons/directional-navigation/arrow/left.svg'
import IconArrowRight from '~icons/directional-navigation/arrow/right.svg'

export default {
  name: 'VariantCarousel',

  components: {
    Splide,
    SplideSlide,
    SplideTrack,
    IconArrowLeft,
    IconArrowRight,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        enableColourSwatch: true,
        description: '',
        price: 0,
        title: '',
      }),
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    relatedProducts: {
      type: Array,
      default: () => [],
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  data() {
    return {
      localProduct: {},
      localColours: [],
      swatches: [],
      showPrevArrow: false,
      showNextArrow: true,
      splideOptions: {
        drag: false,
        type: 'slide',
        pagination: false,
        gap: '6px',
        perPage: 12,
        arrows: true,
        perMove: 1,
        breakpoints: {
          360: {
            perPage: 3,
          },
          480: {
            trimSpace: 'move',
            perPage: 4,
            padding: {
              right: '24px',
              left: '24px',
            },
          },
          1024: {
            flickPower: 130,
            flickMaxPages: 1,
            snap: false,
            type: 'slide',
            gap: '4px',
            arrows: false,
            drag: true,
            perPage: 6,
            padding: {
              right: '24px',
              left: '24px',
            },
          },
          1300: {
            perPage: 5,
          },
          1400: {
            perPage: 6,
          },
          1500: {
            perPage: 7,
          },
          1600: {
            perPage: 8,
          },
          2000: {
            perPage: 9,
          },
        },
      },
    }
  },

  computed: {

    /**
     * Compute color variant title.
     * @returns {String}
     */
    variantColourTitle() {
      const variant = this.variant || this.product?.selectedOrFirstAvailableVariant || this.product?.variants[0]

      const colorVariant = variant?.selectedOptions?.find((option) => {
        const optionName = option.name?.toLowerCase()
        return optionName === 'colour' || optionName === 'color'
      })

      return colorVariant ? colorVariant.value : ''
    },

    /**
     * Compute total number of related colors.
     * @returns {Number}
     */
    variantColourTotal() {
      if (!this.relatedProducts) {
        return 0
      }

      return this.relatedProducts.length
    },

    /**
     * Compute return variant selected size.
     */
     currentVariantSize() {
      const sizeOption = this.variant?.selectedOptions?.find((option) => {
        return option?.name === 'Size'
      })

      return sizeOption?.value
    }
  },

  mounted() {
    this.localProduct = this.product

    this.localColours = this.relatedProducts?.sort((item) => {
      return item?.handle === this.localProduct?.handle ? -1 : 0
    })

    this.relatedProductSwatches()

    this.$nextTick(() => {
      this.splideArrowsLogic()
    })
  },

  methods: {

    /**
     * Computes when to show splide arrows based on swatch visibility.
     */
    splideArrowsLogic() {
      const splideInstance = this.$refs.variantSplide?.splide

      if (!splideInstance) {
        return
      }

      const updateArrowVisibility = () => {
        const { index, Components } = splideInstance
        const slides = Components.Slides.get()

        setTimeout(() => {
          const end = splideInstance.Components.Elements?.slides[slides.length - 1].classList.contains('is-visible')
          this.showPrevArrow = index > 0
          this.showNextArrow = end
        }, 500)
      }

      splideInstance.on('mounted move resized updated', updateArrowVisibility)
      updateArrowVisibility()
    },

    /**
     * Emits variant change emitting swatch handle.
     * @param {String} swatch - Swatch object.
     * @param {Event} event - Change event.
     */
    async handleVariantChange(swatch, event) {
      try {
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }

        const handle = swatch.url.split('/').pop();

        this.$emit('variant-query', handle);

      } catch (error) {
        throw new Error('Error changing variant', error)
      }
    },

    /**
     * Compute swatch objects for colour selector drawer.
     * @returns {Array}
     */
    relatedProductSwatches() {
      const swatches = this.localColours.map((product) => {
        const colorVariant =
          product.selectedOrFirstAvailableVariant?.selectedOptions.find(
            (option) => {
              const optionName = option.name.toLowerCase()
              return optionName === 'colour' || optionName === 'color'
            },
          )

        return {
          id: product.id,
          available: product.available,
          image: product.swatchImage,
          url: product.url,
          title: colorVariant?.value || '',
          tags: product.tags,
          variants: product.variants,
        }
      })

      this.swatches = swatches
    },

    /**
     * Get colour style.
     * - Data is from metaobject which is filtered and returns a new array if
     * 'colourHandle' and 'optionColour' matches
     * @returns {Object}
     */
     getColourStyle() {

      const variant = this.variant || this.product?.selectedOrFirstAvailableVariant || this.product?.variants[0]

      if (!variant) {
        return false
      }

      const variantColor = variant?.selectedOptions.find(
        (option) => {
          const optionName = option.name.toLowerCase()
          return optionName === 'colour' || optionName === 'color'
        },
      )

      if (!variantColor) {
        return false
      }

      const filteredData = cnvs.colours.filter((data) => {
        const colourHandle = data.handle

        return colourHandle === variantColor.value.toLowerCase().replaceAll(' ', '-')
      })

      const colourPrimary = filteredData[0]?.colour_primary
      const colourSecondary = filteredData[0]?.colour_secondary

      if (colourPrimary && colourSecondary) {
        return {
          background: `-webkit-linear-gradient(left, ${colourPrimary} 50%, ${colourSecondary} 50%)`,
        }
      }

      if (colourPrimary) {
        return {
          background: colourPrimary,
        }
      }

      return false
    },

    /**
     * Get colour image style.
     * - Data is from metaobject which is filtered and returns a new array if
     * 'colourHandle' and 'optionColour' matches
     * @returns {Object}
     */
     getColourImageStyle() {
      const variant = this.variant || this.product?.selectedOrFirstAvailableVariant || this.product?.variants[0]

      if (!variant) {
        return false
      }

      const variantColor = variant?.selectedOptions.find(
        (option) => {
          const optionName = option.name.toLowerCase()
          return optionName === 'colour' || optionName === 'color'
        },
      )

      if (!variantColor) {
        return false
      }

      const filteredData = cnvs.colours.filter((data) => {
        const colourHandle = data.handle

        return colourHandle == variantColor.value.toLowerCase().replaceAll(' ', '-')
      })

      const image = filteredData[0]?.image

      if (image) {
        return {
          backgroundImage: `url('${image}')`,
        }
      }

      return false
    },
  },
}
</script>

<style lang="scss">
@import './variant-carousel';
</style>
